import { useEffect } from 'react';
import { SUPPORTED_LANGUAGES } from '../i18n/languages.js';
import { useTranslation } from '../i18n/I18nContext.jsx';

const SITE_URL = 'https://www.claw-dius.com';
const SITE_NAME = 'Claw-dius';
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const OG_IMAGE_ALT = 'Claw-dius lobster mascot — Voice for OpenClaw';

// BCP-47 locale tags for og:locale
const LOCALE_MAP = {
  en: 'en_US', es: 'es_ES', fr: 'fr_FR', pt: 'pt_BR',
  ru: 'ru_RU', ar: 'ar_SA', hi: 'hi_IN', bn: 'bn_BD',
  zh: 'zh_CN', ja: 'ja_JP',
};

function setMetaTag(attr, value, content) {
  let el = document.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = url;
}

function injectJsonLd(data) {
  let el = document.getElementById('page-jsonld');
  if (!el) {
    el = document.createElement('script');
    el.id = 'page-jsonld';
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Manage hreflang <link> tags for all supported languages.
 * Each page gets one self-referencing canonical plus alternates for every
 * other language, plus x-default pointing to the English variant.
 */
function syncHreflangLinks(path) {
  const CLASS = 'pagemeta-hreflang';

  // Remove previous hreflang links
  document.querySelectorAll(`link.${CLASS}`).forEach((el) => el.remove());

  // Build the bare path (without leading /)
  const bare = path === '/' ? '' : path;

  for (const code of SUPPORTED_LANGUAGES) {
    const el = document.createElement('link');
    el.rel = 'alternate';
    el.hreflang = code;
    el.href = `${SITE_URL}/${code}${bare}`;
    el.className = CLASS;
    document.head.appendChild(el);
  }

  // x-default → English variant
  const xd = document.createElement('link');
  xd.rel = 'alternate';
  xd.hreflang = 'x-default';
  xd.href = `${SITE_URL}/en${bare}`;
  xd.className = CLASS;
  document.head.appendChild(xd);
}

/**
 * PageMeta — lightweight per-page SEO via useEffect.
 *
 * Props:
 *   title        string   — page title (appended with "| Clawdius")
 *   description  string   — meta description
 *   path         string   — path WITHOUT language prefix, e.g. "/pricing"
 *   ogType       string   — Open Graph type, default "website"
 *   jsonLd       object   — optional JSON-LD structured data
 */
export default function PageMeta({
  title,
  description,
  path = '/',
  ogType = 'website',
  jsonLd = null,
}) {
  const { lang } = useTranslation();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Voice for OpenClaw`;
  const langPath = path === '/' ? `/${lang}` : `/${lang}${path}`;
  const canonicalUrl = `${SITE_URL}${langPath}`;

  useEffect(() => {
    document.title = fullTitle;

    if (description) setMetaTag('name', 'description', description);

    // Open Graph
    setMetaTag('property', 'og:site_name', SITE_NAME);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:title', fullTitle);
    if (description) setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:image', OG_IMAGE);
    setMetaTag('property', 'og:image:alt', OG_IMAGE_ALT);
    setMetaTag('property', 'og:locale', LOCALE_MAP[lang] || 'en_US');

    // Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', fullTitle);
    if (description) setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', OG_IMAGE);
    setMetaTag('name', 'twitter:image:alt', OG_IMAGE_ALT);

    setCanonical(canonicalUrl);
    syncHreflangLinks(path);

    if (jsonLd) injectJsonLd(jsonLd);

    // Cleanup hreflang links on unmount / path change
    return () => {
      document.querySelectorAll('link.pagemeta-hreflang').forEach((el) => el.remove());
    };
  }, [fullTitle, description, ogType, canonicalUrl, lang, path, jsonLd]);

  return null;
}
